"use client";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import type { Noop } from "@/types";
import { type MultiLevelNavBarItem } from "../../../types";
import { isGroupNavItem } from "../../../utils";
import { hasPathMatchRecursive } from "../../../utils/getMenuActiveItemIndex";

interface UseNavItemStateProps {
  item: MultiLevelNavBarItem;
  level: number;
  isOpen?: boolean;
  onToggle?: Noop;
}

export function useNavItemState(props: UseNavItemStateProps) {
  const { item, level, isOpen, onToggle } = props;
  const pathname = usePathname();
  const shouldBeInitiallyOpen =
    level > 0 &&
    isGroupNavItem(item) &&
    hasPathMatchRecursive(pathname, item.subMenuItems);

  const [open, setOpen] = useState(shouldBeInitiallyOpen);

  useEffect(() => {
    // Sync with parent's isOpen state for top-level items (level 0)
    if (level === 0 && isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen, level]);

  useEffect(() => {
    if (level > 0 && isGroupNavItem(item)) {
      const shouldBeOpen = hasPathMatchRecursive(pathname, item.subMenuItems);
      if (shouldBeOpen) {
        setOpen(true);
      }
    }
  }, [pathname, item, level]);

  const handleToggle = () => {
    if (level === 0 && onToggle) {
      onToggle();
    } else {
      setOpen(!open);
    }
  };

  return { open, handleToggle };
}
